<template>
  <div class="login-page">
    <!-- ══ 左：品牌面 ════════════════════════════════════════ -->
    <section class="brand-pane">
      <div class="brand-glow brand-glow--rose" />
      <div class="brand-glow brand-glow--coral" />

      <div class="brand-top">
        <div class="brand-mark">
          <img :src="logo" alt="" />
        </div>
        <div class="brand-text">
          <h1 class="brand-name">{{ name }}</h1>
          <p class="brand-desc">{{ desc }}</p>
        </div>
      </div>

      <!-- 纸条意象：这一屏的记忆点，同时也是产品本身的形状 -->
      <div class="note-stack" aria-hidden="true">
        <span class="note note--back" />
        <span class="note note--mid" />
        <span class="note note--front">
          <i class="note-line note-line--1" />
          <i class="note-line note-line--2" />
          <i class="note-line note-line--3" />
          <svg class="note-heart" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 20.4S3.6 15.3 3.6 9.6a4.7 4.7 0 0 1 8.4-2.9 4.7 4.7 0 0 1 8.4 2.9c0 5.7-8.4 10.8-8.4 10.8z"
              fill="currentColor"
            />
          </svg>
        </span>
      </div>

      <div class="brand-foot">
        <span>小白网络科技工作室</span>
        <span v-if="version" class="brand-foot__sep">·</span>
        <span v-if="version">v{{ version }}</span>
      </div>
    </section>

    <!-- ══ 右：表单面 ════════════════════════════════════════ -->
    <section class="form-pane">
      <div class="form-box">
        <header class="form-head">
          <h2>欢迎回来</h2>
          <p>使用管理员账号登录{{ name }}运营后台</p>
        </header>

        <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          label-position="top"
          @submit.native.prevent
        >
          <el-form-item prop="username">
            <span slot="label" class="field-label">账号</span>
            <el-input
              ref="username"
              v-model="loginForm.username"
              placeholder="请输入管理员账号"
              prefix-icon="el-icon-user"
              auto-complete="off"
              @keyup.enter.native="handleLogin"
            />
          </el-form-item>

          <el-form-item prop="password">
            <span slot="label" class="field-label">密码</span>
            <el-input
              v-model="loginForm.password"
              type="password"
              placeholder="请输入登录密码"
              prefix-icon="el-icon-lock"
              show-password
              @keyup.enter.native="handleLogin"
            />
          </el-form-item>

          <el-button
            :loading="loading"
            type="primary"
            class="login-btn"
            @click.native.prevent="handleLogin"
          >
            {{ loading ? '登录中' : '登 录' }}
          </el-button>
        </el-form>

        <p class="form-foot">
          <i class="el-icon-lock" />
          仅限授权管理员访问，操作全程留痕
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { login } from '@/api/auth'
import { resolveLogo, formatPageTitle } from '@/utils/system-info'
import Cookies from 'js-cookie'

export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [{ required: true, message: '请输入管理员账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入登录密码', trigger: 'blur' }]
      },
      loading: false
    }
  },
  computed: {
    ...mapGetters(['systemInfo']),
    /** 系统名 / 说明 / logo 全部来自 system_configs，运营改了这里立刻跟着变 */
    name() {
      return this.systemInfo.name
    },
    desc() {
      return this.systemInfo.desc
    },
    version() {
      return this.systemInfo.version
    },
    logo() {
      return resolveLogo(this.systemInfo.logo)
    }
  },
  created() {
    // 未登录状态也能读（/api/system/config/public 免鉴权），失败时内部已兜底
    this.$store.dispatch('loadSystemInfo')
  },
  watch: {
    // 系统名是异步取回来的，取到后标签页标题要跟着换成配置里的名字
    name: {
      handler() {
        this.applyPageTitle()
      },
      immediate: true
    }
  },
  methods: {
    applyPageTitle() {
      document.title = formatPageTitle(this.$route.meta.title, this.name)
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (!valid) {
          return
        }
        this.loading = true
        login(this.loginForm)
          .then(res => {
            Cookies.set('admin_token', res.data.token)
            this.$store.dispatch('setAdminInfo', res.data.adminInfo)
            this.$router.push('/')
            this.$message.success('登录成功')
          })
          .catch(() => {
            this.loading = false
          })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
/* ─────────────────────────────────────────────────────────────
   登录页：左品牌面 / 右表单面
   视觉重心在左侧的系统名与纸条图形上，右侧只承担"把账号密码填完"
   这一件事 —— 两栏都不堆装饰，靠留白与层级差撑专业感。
   ───────────────────────────────────────────────────────────── */
.login-page {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background: var(--paper);
}

/* ══ 左：品牌面 ══════════════════════════════════════════ */
.brand-pane {
  position: relative;
  flex: 0 0 46%;
  max-width: 680px;
  min-width: 400px;
  padding: 56px 56px 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: var(--ink-900);
  overflow: hidden;

  /* 细网格：给大面积深色一点质感，避免死平 */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
    background-size: 64px 64px;
    pointer-events: none;
  }
}

.brand-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(10px);

  &--rose {
    top: -180px;
    left: -140px;
    width: 560px;
    height: 560px;
    background: radial-gradient(circle, rgba(225, 77, 104, 0.26) 0%, transparent 68%);
  }

  &--coral {
    bottom: -220px;
    left: 120px;
    width: 520px;
    height: 520px;
    background: radial-gradient(circle, rgba(255, 138, 101, 0.16) 0%, transparent 66%);
  }
}

.brand-top {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 18px;
  animation: rise 0.5s ease-out both;

  .brand-mark {
    flex: none;
    width: 58px;
    height: 58px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .brand-name {
    margin: 0;
    font-size: 30px;
    font-weight: 600;
    letter-spacing: 2px;
    color: #fff;
    line-height: 1.25;
  }

  .brand-desc {
    margin: 8px 0 0;
    font-size: 13.5px;
    letter-spacing: 0.5px;
    color: var(--ink-350);
  }
}

/* 纸条：三张层叠，只有最上面那张有"内容"，其余只作纸叠的厚度感 */
.note-stack {
  position: relative;
  z-index: 1;
  align-self: center;
  width: 300px;
  height: 300px;
  margin: 12px 0;
  animation: rise 0.7s 0.1s ease-out both;

  .note {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 176px;
    height: 228px;
    border-radius: 14px;
    transform-origin: center;
  }

  .note--back {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.06);
    transform: translate(-50%, -50%) rotate(-16deg) translateX(-42px);
  }

  .note--mid {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.08);
    transform: translate(-50%, -50%) rotate(-4deg);
  }

  .note--front {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 30px 24px;
    background: linear-gradient(160deg, #fffdfc 0%, var(--rose-50) 100%);
    box-shadow: 0 30px 60px -24px rgba(0, 0, 0, 0.6);
    transform: translate(-50%, -50%) rotate(7deg) translateX(44px);
  }

  .note-line {
    display: block;
    height: 6px;
    border-radius: 3px;
    background: rgba(23, 18, 25, 0.1);
    margin-bottom: 14px;

    &--1 { width: 74%; }
    &--2 { width: 92%; }
    &--3 { width: 56%; }
  }

  .note-heart {
    width: 30px;
    height: 30px;
    margin-top: auto;
    color: var(--rose-400);
    align-self: flex-end;
  }
}

.brand-foot {
  position: relative;
  z-index: 2;
  font-size: 12px;
  letter-spacing: 0.5px;
  color: var(--ink-400);

  &__sep {
    margin: 0 6px;
  }
}

/* ══ 右：表单面 ══════════════════════════════════════════ */
.form-pane {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
  /* 极淡的玫瑰光晕：让右侧纸色不至于死平，同时与左侧品牌面同源 */
  background-color: var(--paper);
  background-image: radial-gradient(560px 360px at 92% 6%, rgba(225, 77, 104, 0.05) 0%, transparent 62%);
}

.form-box {
  width: 100%;
  max-width: 356px;
  animation: rise 0.5s 0.05s ease-out both;
}

.form-head {
  margin-bottom: 30px;

  h2 {
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: 1px;
    color: var(--ink-900);
  }

  p {
    margin: 10px 0 0;
    font-size: 13px;
    line-height: 1.6;
    color: var(--ink-500);
  }
}

.login-form {
  ::v-deep .el-form-item {
    margin-bottom: 20px;
  }

  ::v-deep .el-form-item__label {
    padding: 0 0 7px;
    line-height: 1.4;
  }

  .field-label {
    font-size: 12.5px;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: var(--ink-500);
  }

  ::v-deep .el-input__inner {
    height: 46px;
    line-height: 46px;
    padding-left: 42px;
    font-size: 14.5px;
    color: var(--ink-900);
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: var(--r-md);
    transition: border-color 0.16s, box-shadow 0.16s;
  }

  ::v-deep .el-input__inner::placeholder {
    color: var(--ink-350);
  }

  ::v-deep .el-input__inner:hover {
    border-color: var(--ink-300);
  }

  ::v-deep .el-input__inner:focus {
    border-color: var(--rose-400);
    box-shadow: 0 0 0 3px rgba(225, 77, 104, 0.12);
  }

  ::v-deep .el-input__prefix {
    left: 14px;
    width: 20px;
    font-size: 15px;
    line-height: 46px;
    color: var(--ink-350);
    transition: color 0.16s;
  }

  ::v-deep .el-input__suffix {
    .el-input__icon {
      line-height: 46px;
      color: var(--ink-350);
    }
  }

  /* 聚焦时图标跟着变色（__prefix 在 input 之前，用 :focus-within 才能选到） */
  ::v-deep .el-input:focus-within .el-input__prefix,
  ::v-deep .el-form-item.is-error .el-input__prefix {
    color: var(--rose-500);
  }

  ::v-deep .el-form-item__error {
    padding-top: 4px;
    font-size: 12px;
    color: var(--danger);
  }

  .login-btn {
    width: 100%;
    height: 46px;
    margin-top: 8px;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 6px;
    text-indent: 6px;
    color: #fff;
    border: none;
    border-radius: var(--r-md);
    background: linear-gradient(135deg, var(--rose-400) 0%, var(--rose-600) 100%);
    box-shadow: 0 12px 24px -12px rgba(168, 43, 68, 0.7);
    transition: transform 0.16s, box-shadow 0.16s, filter 0.16s;

    &:hover,
    &:focus {
      color: #fff;
      background: linear-gradient(135deg, var(--rose-400) 0%, var(--rose-700) 100%);
      transform: translateY(-1px);
      box-shadow: 0 16px 30px -12px rgba(168, 43, 68, 0.8);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 8px 18px -12px rgba(168, 43, 68, 0.7);
    }

    ::v-deep i {
      font-size: 15px;
    }
  }
}

.form-foot {
  margin: 34px 0 0;
  padding-top: 18px;
  border-top: 1px solid var(--ink-200);
  font-size: 12px;
  letter-spacing: 0.3px;
  color: var(--ink-500);

  i {
    margin-right: 5px;
    font-size: 12px;
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ══ 窄屏：左栏收成顶部品牌条，纸条图形让位给表单 ═══════════ */
@media (max-width: 900px) {
  .login-page {
    flex-direction: column;
  }

  .brand-pane {
    flex: none;
    min-width: 0;
    max-width: none;
    width: 100%;
    padding: 32px 28px 28px;
    justify-content: flex-start;
    gap: 20px;
  }

  .brand-top {
    .brand-mark {
      width: 48px;
      height: 48px;
      border-radius: 14px;
    }

    .brand-name {
      font-size: 23px;
    }

    .brand-desc {
      margin-top: 6px;
      font-size: 12.5px;
    }
  }

  .note-stack {
    display: none;
  }

  .brand-foot {
    font-size: 11.5px;
  }

  .form-pane {
    flex: 1;
    align-items: flex-start;
    padding: 36px 28px 48px;
  }

  .form-box {
    max-width: 420px;
  }
}
</style>
